import React, { useState } from 'react';
import { warrantyLookupApi, formatApiErrorDetail } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, ShieldCheck, ShieldX, Smartphone, User, Loader2, Store } from 'lucide-react';
import { toast } from 'sonner';

export default function WarrantyLookupPage() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await warrantyLookupApi.search(query.trim());
      setResult(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Search failed');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Warranty Lookup</h1>
          <p className="text-slate-600 mt-1">Search by IMEI number or warranty certificate number</p>
        </div>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter IMEI (15 digits) or Certificate # (e.g., WC-...)"
                  className="pl-10 h-12 text-base"
                  data-testid="warranty-search-input"
                />
              </div>
              <Button type="submit" className="bg-[#005DE8] hover:bg-[#0047B3] h-12 px-6" disabled={loading} data-testid="warranty-search-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {searched && !loading && result && !result.found && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <ShieldX className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No results found</h3>
              <p className="text-slate-500 mt-1">No warranty or sale found for "{query}"</p>
            </CardContent>
          </Card>
        )}

        {result?.found && (
          <div className="space-y-4">
            {/* Warranty Status */}
            {result.warranty && (
              <Card className={`border-2 ${result.warranty.is_active ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'}`} data-testid="warranty-result">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <ShieldCheck className={`h-5 w-5 ${result.warranty.is_active ? 'text-emerald-600' : 'text-red-500'}`} />
                      Warranty {result.warranty.status}
                    </CardTitle>
                    <Badge className={result.warranty.is_active
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-300 border'
                      : 'bg-red-100 text-red-800 border-red-300 border'
                    }>
                      {result.warranty.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-500">Certificate:</span> <span className="font-mono font-medium">{result.warranty.certificate_number}</span></div>
                    <div><span className="text-slate-500">Plan:</span> <span className="font-medium">{result.warranty.plan_name}</span></div>
                    <div><span className="text-slate-500">Start:</span> <span className="font-medium">{formatDate(result.warranty.start_date)}</span></div>
                    <div><span className="text-slate-500">End:</span> <span className="font-medium">{formatDate(result.warranty.end_date)}</span></div>
                    <div><span className="text-slate-500">Price:</span> <span className="font-medium">{formatCurrency(result.warranty.warranty_price)}</span></div>
                  </div>
                </CardContent>
              </Card>
            )}

            {!result.warranty && result.warranty_attached === false && (
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="py-6 text-center">
                  <ShieldX className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="font-medium text-slate-700">No warranty attached to this sale</p>
                </CardContent>
              </Card>
            )}

            {/* Device & Customer Info */}
            <Card className="border-slate-200">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 flex items-center gap-2 mb-3">
                      <Smartphone className="h-4 w-4" /> Device
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Brand:</span> <span className="font-medium">{result.device?.brand}</span></p>
                      <p><span className="text-slate-500">Model:</span> <span className="font-medium">{result.device?.model}</span></p>
                      <p><span className="text-slate-500">IMEI:</span> <span className="font-mono">{result.device?.imei}</span></p>
                      <p><span className="text-slate-500">Price:</span> <span className="font-medium">{formatCurrency(result.device?.price || 0)}</span></p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 flex items-center gap-2 mb-3">
                      <User className="h-4 w-4" /> Customer
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Name:</span> <span className="font-medium">{result.customer?.name}</span></p>
                      <p><span className="text-slate-500">Phone:</span> <span className="font-medium">{result.customer?.phone}</span></p>
                    </div>
                    <h4 className="text-sm font-medium text-slate-500 flex items-center gap-2 mb-2 mt-4">
                      <Store className="h-4 w-4" /> Sold at
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Shop:</span> <span className="font-medium">{result.tenant}</span></p>
                      <p><span className="text-slate-500">Branch:</span> <span className="font-medium">{result.branch}</span></p>
                      {result.sale_date && <p><span className="text-slate-500">Date:</span> <span className="font-medium">{formatDate(result.sale_date)}</span></p>}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
